import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, ShieldAlert, UserPlus, Ban, RotateCcw, KeyRound } from "lucide-react";
import { api, errMsg, fmtDateTime } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

function AdminRow({ a, act }) {
  const suspended = a.status === "suspended";
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white p-3 text-sm" data-testid={`sa-admin-${a.user_id}`}>
      <div>
        <p className="font-semibold text-slate-900">{a.name || "-"} {a.is_me && <span className="ml-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-800">Tu</span>}</p>
        <p className="text-xs text-slate-500">{a.email} · {a.promoted_at ? `promosso il ${fmtDateTime(a.promoted_at)}` : `creato il ${fmtDateTime(a.created_at)}`}</p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <span data-testid={`sa-admin-status-${a.user_id}`} className={`rounded-full px-2.5 py-1 text-[11px] font-bold ${suspended ? "bg-rose-100 text-rose-700" : "bg-emerald-100 text-emerald-800"}`}>{suspended ? "Sospeso" : "Attivo"}</span>
        <span data-testid={`sa-admin-mfa-${a.user_id}`} className={`flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold ${a.mfa_enabled ? "bg-emerald-50 text-emerald-700" : "bg-amber-100 text-amber-800"}`}>
          {a.mfa_enabled ? <ShieldCheck className="h-3.5 w-3.5" /> : <ShieldAlert className="h-3.5 w-3.5" />}{a.mfa_enabled ? "Authenticator attivo" : "Da configurare al primo accesso"}
        </span>
        {!a.is_me && (
          <>
            {suspended
              ? <Button size="sm" variant="outline" data-testid={`sa-admin-reactivate-${a.user_id}`} onClick={() => act(`/superadmin/admins/${a.user_id}/reactivate`, "Super Admin riattivato")}><RotateCcw className="mr-1 h-3.5 w-3.5" />Riattiva</Button>
              : <Button size="sm" variant="outline" className="text-rose-700" data-testid={`sa-admin-suspend-${a.user_id}`} onClick={() => act(`/superadmin/admins/${a.user_id}/suspend`, "Super Admin sospeso")}><Ban className="mr-1 h-3.5 w-3.5" />Sospendi</Button>}
            <Button size="sm" variant="outline" data-testid={`sa-admin-reset-mfa-${a.user_id}`} onClick={() => window.confirm("Azzerare l'autenticazione a due fattori? Al prossimo accesso dovrà configurare un nuovo Google Authenticator.") && act(`/superadmin/admins/${a.user_id}/reset-mfa`, "2FA azzerata")}><KeyRound className="mr-1 h-3.5 w-3.5" />Azzera 2FA</Button>
          </>
        )}
      </div>
    </div>
  );
}

export function SuperAdmins() {
  const [items, setItems] = useState([]);
  const [code, setCode] = useState("");
  const [email, setEmail] = useState("");
  const load = () => api.get("/superadmin/admins").then(({ data }) => setItems(data));
  useEffect(() => {
    load();
  }, []);
  const act = async (url, ok, extra = {}) => {
    if (!/^\d{6}$/.test(code)) return toast.error("Inserisci il codice a 6 cifre di Google Authenticator");
    try {
      await api.post(url, { mfa_code: code, ...extra });
      toast.success(ok);
      setCode("");
      setEmail("");
      load();
    } catch (e) {
      toast.error(errMsg(e));
      setCode("");
    }
  };
  return (
    <div className="space-y-4" data-testid="sa-admins">
      <div className="glass flex flex-wrap items-end gap-3 rounded-2xl p-4">
        <div className="space-y-1"><Label>Codice Google Authenticator (conferma azioni)</Label><Input data-testid="sa-admin-code" inputMode="numeric" maxLength={6} className="w-48 tracking-[0.3em]" placeholder="000000" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} /></div>
        <div className="space-y-1"><Label>Email utente registrato da promuovere</Label><Input data-testid="sa-admin-promote-email" className="w-72" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="nome@esempio.it" /></div>
        <Button data-testid="sa-admin-promote-btn" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => act("/superadmin/admins/promote", "Utente promosso a Super Admin", { email })}><UserPlus className="mr-1 h-4 w-4" />Promuovi a Super Admin</Button>
        <p className="w-full text-xs text-slate-500">Al primo accesso il nuovo Super Admin dovrà scansionare un QR code con Google Authenticator. Ogni azione richiede un codice nuovo.</p>
      </div>
      {items.map((a) => <AdminRow key={a.user_id} a={a} act={act} />)}
    </div>
  );
}
